import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import AuthActionPage from './pages/AuthActionPage';
import DashboardPage from './pages/DashboardPage';
import BracketPage from './pages/BracketPage';
import LeaderboardPage from './pages/LeaderboardPage';
import ScoresPage from './pages/ScoresPage';
import AdminPage from './pages/AdminPage';
import ArchivePage from './pages/ArchivePage';
import PaymentPage from './pages/PaymentPage';
import PayoutPage from './pages/PayoutPage';
import './styles.css';

// Shared context so Layout and other components know bracket/observer status
export const BracketAccessContext = createContext({ hasBracket: false, pastDeadline: false });

function PrivateRoute({ children }) {
  const { currentUser } = useAuth();
  return currentUser ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const { currentUser, isAdmin } = useAuth();
  if (!currentUser) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

// Guard for pages requiring a submitted bracket (or observer/admin/past deadline)
function BracketRoute({ children }) {
  const { isAdmin, userProfile } = useAuth();
  const { hasBracket } = useContext(BracketAccessContext);
  if (isAdmin || hasBracket || userProfile?.isObserver) return children;
  return <Navigate to="/" replace state={{ needsBracket: true }} />;
}

function AppRoutes() {
  const { currentUser, isAdmin, userProfile } = useAuth();
  const [hasBracket, setHasBracket] = useState(false);
  const [pastDeadline, setPastDeadline] = useState(false);
  const API = process.env.REACT_APP_API_URL || '/api';

  // Check deadlines
  useEffect(() => {
    fetch(`${API}/deadlines`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.regularDeadline) setPastDeadline(new Date() > new Date(d.regularDeadline)); })
      .catch(() => {});
  }, []);

  // Check if user has any submitted brackets (only needed if not admin/observer)
  useEffect(() => {
    if (!currentUser || isAdmin || userProfile?.isObserver) return;
    currentUser.getIdToken()
      .then(token => fetch(`${API}/brackets`, { headers: { Authorization: `Bearer ${token}` } }))
      .then(r => r.ok ? r.json() : [])
      .then(brackets => setHasBracket(Array.isArray(brackets) && brackets.length > 0))
      .catch(() => {});
  }, [currentUser, isAdmin, userProfile]);

  return (
    <BracketAccessContext.Provider value={{ hasBracket, pastDeadline, setHasBracket }}>
      <Routes>
        <Route path="/login" element={currentUser ? <Navigate to="/" /> : <LoginPage />} />
        <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
          <Route index element={<DashboardPage />} />
          <Route path="bracket/new" element={<BracketPage />} />
          <Route path="bracket/:id" element={<BracketPage />} />
          <Route path="leaderboard" element={<BracketRoute><LeaderboardPage /></BracketRoute>} />
          <Route path="scores" element={<BracketRoute><ScoresPage /></BracketRoute>} />
          <Route path="payment" element={<PrivateRoute><PaymentPage /></PrivateRoute>} />
          <Route path="payout" element={<BracketRoute><PayoutPage /></BracketRoute>} />
          <Route path="archive" element={<BracketRoute><ArchivePage /></BracketRoute>} />
          <Route path="admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
        </Route>
      </Routes>
    </BracketAccessContext.Provider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
